import React, { Component, Fragment } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';

import World, { Entity, ASSET_MODE_PER_ENTITY, DIRECTION_NORTH, ASSET_MODE_PER_CELL } from '../shared/world';
import Environment from '../shared/environment';
import AssetLibrary from '../shared/asset-library';
import { initializeFirebase } from '../shared/firebase';
import Preview, { PREVIEW_MODE_LEFT_RIGHT } from './preview';
import { copyText, debounce } from '../shared/browser-utils';
import { SELECT_MODE_ADD, SELECT_MODE_REMOVE, SELECT_MODE_REPLACE, SELECT_MODE_TOGGLE } from './canvas';
import Outliner, { OUTLINER_MODE_LAYERS, OUTLINER_MODE_LIBRARY, OUTLINER_MODE_ENVIRONMENT } from './outliner';
import LayerProperties from './layer-properties';
import AssetProperties from './asset-properties';
import Resizer, { RESIZER_EXPAND_LAST, RESIZER_ORIENTATION_HORIZONTAL, RESIZER_ORIENTATION_VERTICAL } from '../shared/resizer';
import './editor.css';

initializeFirebase();
const firebaseAuthProvider = new firebase.auth.GoogleAuthProvider();

const SAVE_STATE_SAVED = 'Saved';
const SAVE_STATE_SAVING = 'Saving...';
//const SAVE_STATE_ERROR = 'Error Saving.';

function Header({ user, onSignIn, saveState, saveError }) {
  return (
    <header className="editor__header">
      <span className="editor__save-state">
        {saveState} {saveError}
      </span>
      <span className="editor__user">{user && user.email}</span>
      {!user && (
        <button className="editor__sign-in" onClick={onSignIn}>
          Sign In
        </button>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer__help">
        Left-Click: Select, Double Click: Select Entity: Right-Click: Pan, Spacebar+drag: Pan, Scroll: Zoom, G: Move Selected Entities,
        Shift-D: Duplicate
      </div>
    </footer>
  );
}

class UndoableEdit {
  constructor(app, description) {
    this.description = description;
    this.app = app;
    this.undoState = this.saveState();
  }

  undo() {
    if (!this.redoState) {
      this.redoState = this.saveState();
    }
    return this.restoreState(this.undoState);
  }

  redo() {
    return this.restoreState(this.redoState);
  }

  saveState() {
    return {
      world: this.app.state.world.clone(),
    };
  }

  restoreState(undoState) {
    return { world: undoState.world, selection: new Set() };
  }
}

export default class Editor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      worldName: undefined,
      world: null,
      environment: new Environment(),
      version: 1,
      saveState: SAVE_STATE_SAVED,
      saveError: '',
      assetLibrary: null,
      assetGhost: null,
      selection: new Set([]),
      selectedAsset: null,
      activeLayer: null,
      user: null,
      previewMode: PREVIEW_MODE_LEFT_RIGHT,
      outlinerMode: OUTLINER_MODE_LAYERS,
    };
    this._history = [];
    this._historyIndex = -1;
    this._shiftDown = false;
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onPaste = this._onPaste.bind(this);
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseDown);
    window.addEventListener('paste', this._onPaste);
    this._onSignIn = this._onSignIn.bind(this);
    this._onChangePreviewMode = this._onChangePreviewMode.bind(this);
    this._onChangeOutlinerMode = this._onChangeOutlinerMode.bind(this);
    this._onSelectEntity = this._onSelectEntity.bind(this);
    this._onSelectCells = this._onSelectCells.bind(this);
    this._onSelectAsset = this._onSelectAsset.bind(this);
    this._onCreateAssetGhost = this._onCreateAssetGhost.bind(this);
    this._onPlaceAsset = this._onPlaceAsset.bind(this);
    this._onChangeEntityProp = this._onChangeEntityProp.bind(this);
    this._onExtendEntity = this._onExtendEntity.bind(this);
    this._onRemoveCells = this._onRemoveCells.bind(this);
    this._onMoveCells = this._onMoveCells.bind(this);
    this._onDuplicateCells = this._onDuplicateCells.bind(this);
    this._onChangeAssets = this._onChangeAssets.bind(this);
    this._onChangeDirections = this._onChangeDirections.bind(this);
    this._onJoinEntities = this._onJoinEntities.bind(this);
    this._onToggleLayerVisibility = this._onToggleLayerVisibility.bind(this);
    this._onToggleLayerExpanded = this._onToggleLayerExpanded.bind(this);
    this._onSetActiveLayer = this._onSetActiveLayer.bind(this);
    this._onCreateLayer = this._onCreateLayer.bind(this);
    this._onAssetPropertyChange = this._onAssetPropertyChange.bind(this);
    this._onChangeEnvironment = this._onChangeEnvironment.bind(this);
    this._debouncedSaveWorld = debounce(this._saveWorld.bind(this), 5000);
  }

  componentWillUnmount() {
    console.log('will unmount');
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseDown);
    window.removeEventListener('paste', this._onPaste);
  }

  async componentDidMount() {
    const worldMatch = document.location.search.match(/world=(\w+)/);
    let worldName = 'test';
    if (worldMatch) {
      worldName = worldMatch[1];
    }
    firebase.auth().onAuthStateChanged(() => {
      this.setState({ user: firebase.auth().currentUser });
    });
    this.setState({ worldName });
    this._startWorldLoader(worldName);
  }

  async _startWorldLoader(worldName) {
    const db = firebase.firestore();
    db.collection('worlds')
      .doc(worldName)
      .onSnapshot(async (doc) => {
        const json = JSON.parse(doc.data().json);
        const world = World.fromJSON(json);
        if (!this.state.world || this.state.world.assetLibrary !== world.assetLibrary) {
          this._startAssetLibraryLoader(world.assetLibrary);
        }
        let activeLayer;
        if (this.state.activeLayer) {
          activeLayer = world.layers.find((layer) => layer.name === this.state.activeLayer.name);
        } else {
          activeLayer = world.layers.find((layer) => layer.editorVisible);
          if (!activeLayer) activeLayer = world.layers[0];
        }
        // Set visibility of layers according to visibility in editor.
        world.layers.forEach((layer) => {
          layer.visible = layer.editorVisible;
        });
        this.setState({ world, activeLayer });
      });
  }

  async _startAssetLibraryLoader(assetLibraryName) {
    const db = firebase.firestore();
    db.collection('asset-libraries')
      .doc(assetLibraryName)
      .onSnapshot(async (doc) => {
        const json = JSON.parse(doc.data().json);
        const assetLibrary = await AssetLibrary.fromJSON(json);
        this.setState({ assetLibrary });
      });
  }

  async _saveWorld() {
    console.assert(this.state.worldName, 'worldName is not set.');
    console.assert(this.state.world, 'world is not loaded yet.');
    const { worldName } = this.state;
    const json = JSON.stringify(this.state.world.toJSON());
    await firebase.firestore().collection('worlds').doc(worldName).set({ json });
    this.setState({ saveState: SAVE_STATE_SAVED });
  }

  saveWorld() {
    if (!this.state.user) {
      this._bumpVersion();
      return;
    }
    this.setState({ saveState: SAVE_STATE_SAVING });
    this._debouncedSaveWorld();
    this._bumpVersion();
  }

  _onKeyDown(e) {
    if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this._undo();
    } else if (e.key === 'y' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this._redo();
    } else if (e.key === 'e' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this._onExport();
    }
  }

  _onKeyUp(e) {
    if (e.keyCode === 32) {
      this._spaceDown = false;
    }
  }

  _onMouseDown(e) {
    this._shiftDown = e.shiftKey;
  }

  _onMouseUp(e) {
    this._shiftDown = e.shiftKey;
  }

  _onPaste(e) {
    const text = e.clipboardData.getData('text');
    try {
      const world = World.fromJSON(JSON.parse(text));
      this.setState({
        world,
        selection: new Set(),
      });
      this._bumpVersion();
      this.forceUpdate();
    } catch (e) {
      console.error(e);
    }
  }

  _onExport() {
    const json = this.state.world.toJSON();
    copyText(JSON.stringify(json, null, 2));
    this.setState({
      toastVisible: true,
      toastMessage: 'Code copied to clipboard.',
    });
    window.setTimeout(() => {
      this.setState({ toastVisible: false });
    }, 2000);
  }

  _bumpVersion() {
    this.setState({ version: this.state.version + 1 });
  }

  _undo() {
    const edit = this._history[this._historyIndex];
    if (!edit) return;
    this._historyIndex -= 1;
    this.setState(edit.undo());
    this._bumpVersion();
  }

  _redo() {
    const edit = this._history[this._historyIndex + 1];
    if (!edit) return;
    this._historyIndex += 1;
    this.setState(edit.redo());
    this._bumpVersion();
  }

  _addEdit(description) {
    // Remove all future updates (after we start doing new things after undo).
    this._history.splice(this._historyIndex + 1, this._history.length - this._historyIndex);
    this._history.push(new UndoableEdit(this, description));
    this._historyIndex = this._history.length - 1;
  }

  async _onSignIn() {
    await firebase.auth().signInWithPopup(firebaseAuthProvider);
    const user = firebase.auth().currentUser;
    this.setState({ user });
  }

  _onChangePreviewMode(previewMode) {
    this.setState({ previewMode });
  }

  _onChangeOutlinerMode(outlinerMode) {
    this.setState({ outlinerMode });
  }

  _onClearSelection() {
    this.setState({ selection: new Set() });
  }

  _onSelectEntity(entity) {
    this.setState({ selection: new Set(entity.cellIds) });
  }

  _onSelectCells(cellIds, mode = SELECT_MODE_REPLACE) {
    if (mode === SELECT_MODE_REPLACE) {
      this.setState({ selection: new Set(cellIds) });
    } else if (mode === SELECT_MODE_ADD) {
      for (const cellId of cellIds) {
        this.state.selection.add(cellId);
      }
      this.forceUpdate();
    } else if (mode === SELECT_MODE_REMOVE) {
      for (const cellId of cellIds) {
        this.state.selection.delete(cellId);
      }
      this.forceUpdate();
    } else if (mode === SELECT_MODE_TOGGLE) {
      for (const cellId of cellIds) {
        if (this.state.selection.has(cellId)) {
          this.state.selection.delete(cellId);
        } else {
          this.state.selection.add(cellId);
        }
      }
      this.forceUpdate();
    } else {
      throw new Error(`Unsupported select mode ${mode}`);
    }
  }

  _onSelectAsset(asset) {
    this.setState({ selectedAsset: asset });
  }

  // Start dragging a new asset.
  _onCreateAssetGhost(e, asset) {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;

    const onMouseMove = (e) => {
      if (Math.abs(startX - e.clientX) > 5 && Math.abs(startY - e.clientY) > 5) {
        this.setState({ assetGhost: { asset } });
        window.removeEventListener('mousemove', onMouseMove);
      }
    };

    const onMouseUp = () => {
      this.setState({ assetGhost: null });
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  _onPlaceAsset(asset, pt) {
    const { world, activeLayer } = this.state;
    // Double-check that we can place the asset where we want.
    const occupied = activeLayer.boxOccupied(pt.x, pt.y, pt.x + asset.width - 1, pt.y + asset.height - 1);
    if (!occupied) {
      this._addEdit('Create new Entity');
      // Place the item in the world.
      const cellIds = world.boxCellIds(pt.x, pt.y, pt.x + asset.width - 1, pt.y + asset.height - 1);
      const assetId = world.ensureAsset(asset.name);
      const assetIds = Array(cellIds.length).fill(assetId);
      const directions = Array(cellIds.length).fill(DIRECTION_NORTH);
      const entity = new Entity(world.generateEntityId(), asset.kind, cellIds, assetIds, directions);
      activeLayer.addEntity(entity);
      this.saveWorld();
    }
    this.setState({ assetGhost: null });
  }

  _moveEntities(dx, dy) {
    const world = this.state.world;
    //const cellIds = Array.from(this.state.selection);
    const selectedEntities = world.findEntitiesForCells(this.state.selection);
    const cellMap = world._cellMap.slice();
    // Remove all cell entities from cell map
    for (const entity of selectedEntities) {
      entity.cellIds.forEach((id) => {
        cellMap[id] = 0;
      });
    }
    // Try to add cell entities to cell map. Check if they would fit.
    for (const entity of selectedEntities) {
      for (const cellId of entity.cellIds) {
        const pt = world.cellIdToPoint(cellId);
        pt.x += dx;
        pt.y += dy;
        const newCellId = world.pointToCellId(pt);
        if (cellMap[newCellId] !== 0) {
          console.error(`Cell ${pt.x},${pt.y} is not empty.`);
          return;
        }
      }
    }

    // Actually move the entities
    for (const entity of selectedEntities) {
      const newCellIds = [];
      for (const cellId of entity.cellIds) {
        const newCellId = world.cellIdMoved(cellId, dx, dy);
        cellMap[newCellId] = entity.id;
        newCellIds.push(newCellId);
      }
      entity.cellIds = newCellIds;
    }
    world._cellMap = cellMap;

    // Move the selected cell ids.
    let selectedCellIds = Array.from(this.state.selection);
    selectedCellIds = selectedCellIds.map((cellId) => world.cellIdMoved(cellId, dx, dy));
    this.setState({ selection: new Set(selectedCellIds) });
    this.saveWorld();
  }

  _onChangeEntityProp(entity, propName, value) {
    this._addEdit('Change Entity Property');
    entity.setPropertyValue(propName, value);
    this.saveWorld();
  }

  _onExtendEntity(entity, cellIds) {
    this._addEdit('Extend Entity');
    this.state.world.extendEntity(entity, cellIds);
    this._onClearSelection();
    this.forceUpdate();
    this.saveWorld();
  }

  _onRemoveCells(entity, cellIds) {
    this._addEdit('Remove Cells from Entity');
    this.state.world.removeCellsFromEntity(entity, cellIds);
    this._onClearSelection();
    this.forceUpdate();
    this.saveWorld();
  }

  _onMoveCells(cellIds, dx, dy) {
    // First check if the cells are occupied.
    const [newCellIds, valid] = this.state.world.offsetCellIds(cellIds, dx, dy);
    if (!valid) return;
    // Don't check the cell IDs that are already occupied by us.
    const entirelyNewCellIds = newCellIds.filter((id) => !cellIds.includes(id));
    if (this.state.activeLayer.someCellsOccupied(entirelyNewCellIds)) return;

    this._addEdit('Move Cells');
    const entities = this.state.activeLayer.findEntitiesForCells(cellIds);
    this.state.activeLayer.moveEntities(Array.from(entities), dx, dy);
    this.setState({ selection: new Set(newCellIds) });
    this.saveWorld();
  }

  _onDuplicateCells(cellIds, dx, dy) {
    // First check if the cells are occupied.
    const [newCellIds, valid] = this.state.world.offsetCellIds(cellIds, dx, dy);
    if (!valid) return;
    if (this.state.activeLayer.someCellsOccupied(newCellIds)) return;

    this._addEdit('Duplicate Cells');
    const entities = this.state.activeLayer.findEntitiesForCells(cellIds);
    this.state.activeLayer.duplicateEntities(this.state.world, Array.from(entities), dx, dy);
    this.setState({ selection: new Set(newCellIds) });
    this.saveWorld();
  }

  // Change all assets with the given name to the new asset
  _onChangeAssets(oldAssetName, newAssetName) {
    console.assert(this.state.activeLayer);
    console.assert(this.state.selection.size > 0);
    const cellIds = Array.from(this.state.selection);
    const entities = this.state.activeLayer.findEntitiesForCells(cellIds);
    const oldAssetEntry = Object.entries(this.state.world.assets).find(([k, v]) => v === oldAssetName);
    if (!oldAssetEntry) {
      console.error(`Could not find asset ${oldAssetName} in world.`);
      return;
    }
    this._addEdit('Change Assets');
    const oldAssetId = parseInt(oldAssetEntry[0]);
    const newAssetId = parseInt(this.state.world.ensureAsset(newAssetName));
    for (const entity of Array.from(entities)) {
      if (Entity.assetModeForKind(entity.kind) === ASSET_MODE_PER_ENTITY) {
        entity.assetIds = entity.assetIds.map((id) => (id === oldAssetId ? newAssetId : id));
      } else {
        // Only change assetIds for selected cells.
        for (const cellId of Array.from(this.state.selection)) {
          const index = entity.cellIds.indexOf(cellId);
          if (index >= 0) {
            if (entity.assetIds[index] === oldAssetId) {
              entity.assetIds[index] = newAssetId;
            }
          }
        }
      }
    }
    this.saveWorld();
  }

  _onChangeDirections(newDirection) {
    console.assert(this.state.activeLayer);
    console.assert(this.state.selection.size > 0);
    const cellIds = Array.from(this.state.selection);
    const entities = this.state.activeLayer.findEntitiesForCells(cellIds);
    this._addEdit('Change Assets');
    for (const entity of Array.from(entities)) {
      if (Entity.assetModeForKind(entity.kind) === ASSET_MODE_PER_ENTITY) {
        entity.directions = Array(entity.cellIds.length).fill(newDirection);
      } else {
        for (const cellId of Array.from(this.state.selection)) {
          const index = entity.cellIds.indexOf(cellId);
          if (index >= 0) {
            entity.directions[index] = newDirection;
          }
        }
      }
    }
    this.saveWorld();
  }

  _onJoinEntities(entities) {
    if (entities.length <= 1) {
      console.warn('Not enough entities selected.');
      return;
    }
    const entityKinds = entities.reduce((s, e) => s.add(e.kind), new Set());
    if (entityKinds.size !== 1) {
      console.warn('Multiple entity kinds selected.');
      return;
    }
    const entityKind = Array.from(entityKinds)[0];
    const assetMode = Entity.assetModeForKind(entityKind);
    if (assetMode !== ASSET_MODE_PER_CELL) {
      console.warn(`Selected entity kind ${entityKind} is not per cell.`);
      return;
    }
    // Check that the entities are on the same layer.
    const { activeLayer } = this.state;
    for (const entity of entities) {
      if (!activeLayer.entities.includes(entity)) {
        console.warn(`Entity ${entity.id} is not part of active layer ${this.state.activeLayer.name}.`);
        return;
      }
    }

    this._addEdit('Join Entities');
    debugger;
    const firstEntity = entities[0];
    for (let i = 1; i < entities.length; i++) {
      const entity = entities[i];
      firstEntity.cellIds.push(...entity.cellIds);
      firstEntity.assetIds.push(...entity.assetIds);
      firstEntity.directions.push(...entity.directions);
    }
    activeLayer.entities = activeLayer.entities.filter((e) => e === firstEntity || !entities.includes(e));
    activeLayer._rebuildCellMap();
    this.saveWorld();
  }

  _onToggleLayerVisibility(layer) {
    this._addEdit('Toggle Layer Visibility');
    layer.editorVisible = !layer.editorVisible;
    layer.visible = layer.editorVisible;
    this.saveWorld();
  }

  _onToggleLayerExpanded(layer) {
    layer.editorExpanded = !layer.editorExpanded;
    this.forceUpdate();
  }

  _onSetActiveLayer(activeLayer) {
    this.setState({ activeLayer });
  }

  _onCreateLayer(layerName) {
    this._addEdit('Create Layer');
    const newLayer = this.state.world.createLayer(layerName);
    // This can happen if the name already exists.
    if (!newLayer) {
      alert('Layer with this name already exists.');
      return;
    }
    this.setState({ activeLayer: newLayer });
    this.saveWorld();
    this.forceUpdate();
  }

  _onAssetPropertyChange(asset, property, value) {
    this._addEdit('Asset Property Change');
    asset[property] = value;
    // this.saveAssetLibrary();
  }

  _onChangeEnvironment(name, value) {
    const { environment } = this.state;
    environment[name] = value;
    this.setState({ environment, version: this.state.version + 1 });
  }

  render() {
    const {
      world,
      environment,
      version,
      saveState,
      saveError,
      assetLibrary,
      assetGhost,
      selection,
      selectedAsset,
      activeLayer,
      previewMode,
      outlinerMode,
      user,
    } = this.state;
    return (
      <div className="app editor">
        <Header user={user} onSignIn={this._onSignIn} saveState={saveState} saveError={saveError} />
        <Resizer orientation={RESIZER_ORIENTATION_HORIZONTAL} size={300} expand={RESIZER_EXPAND_LAST}>
          <Preview
            world={world}
            environment={environment}
            version={version}
            assetLibrary={assetLibrary}
            assetGhost={assetGhost}
            selection={selection}
            activeLayer={activeLayer}
            previewMode={previewMode}
            onChangePreviewMode={this._onChangePreviewMode}
            onCreateAssetGhost={this._onCreateAssetGhost}
            onSelectCells={this._onSelectCells}
            onSelectEntity={this._onSelectEntity}
            onPlaceAsset={this._onPlaceAsset}
            onMoveCells={this._onMoveCells}
            onDuplicateCells={this._onDuplicateCells}
          />
          <Resizer orientation={RESIZER_ORIENTATION_VERTICAL} size={300}>
            <Outliner
              world={world}
              environment={environment}
              assetLibrary={assetLibrary}
              selection={selection}
              selectedAsset={selectedAsset}
              activeLayer={activeLayer}
              outlinerMode={outlinerMode}
              onChangeOutlinerMode={this._onChangeOutlinerMode}
              onChangeEnvironment={this._onChangeEnvironment}
              onSelectEntity={this._onSelectEntity}
              onSelectAsset={this._onSelectAsset}
              onCreateAssetGhost={this._onCreateAssetGhost}
              onToggleLayerVisibility={this._onToggleLayerVisibility}
              onToggleLayerExpanded={this._onToggleLayerExpanded}
              onSetActiveLayer={this._onSetActiveLayer}
              onCreateLayer={this._onCreateLayer}
            />
            <Fragment>
              {outlinerMode === OUTLINER_MODE_LAYERS && (
                <LayerProperties
                  world={world}
                  assetLibrary={assetLibrary}
                  selection={selection}
                  activeLayer={activeLayer}
                  onChangeEntityProp={this._onChangeEntityProp}
                  onExtendEntity={this._onExtendEntity}
                  onRemoveCells={this._onRemoveCells}
                  onChangeAssets={this._onChangeAssets}
                  onChangeDirections={this._onChangeDirections}
                  onJoinEntities={this._onJoinEntities}
                />
              )}
              {outlinerMode === OUTLINER_MODE_LIBRARY && (
                <AssetProperties asset={selectedAsset} onAssetPropertyChange={this._onAssetPropertyChange} />
              )}
              {outlinerMode === OUTLINER_MODE_ENVIRONMENT && <div className="props" />}
            </Fragment>
          </Resizer>
        </Resizer>
        <Footer />
      </div>
    );
  }
}
